import React, { useState, useEffect, useCallback, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// antd component
import { Checkbox, Collapse, Button, Empty, Tag } from "antd";

// icons
import CustomIcons from "components/CustomIcons";
import { UndoOutlined, LoadingOutlined } from "@ant-design/icons";

// utils
/* import {
  sortList,
  searchListBy,
  prepearFilteredOptions,
  filterListBy,
} from 'utils/helpers/helper.filter-sort'; */

function FilterOptions({
  uniqueKey,
  options,
  loading,
  onFilter,
  onReset,
  value,
  resetLabel,
  // static
  showIcon,
  view,
  // switch state
  show,
  setShow,
  // default props
  className,
}) {
  const childClassNames = classNames(
    "extra-bar-item",
    "filter-extra",
    className,
  );
  const { t, i18n } = useTranslation();

  const [filter, setFilter] = useState(value || []);

  useEffect(() => {
    setFilter(value);
  }, [value]);

  const toggleFilter = useCallback(() => setShow(prev => !prev), []);

  const onFilterCallback = useCallback(
    checkedValues => {
      setFilter(checkedValues);
      if (!loading) {
        !!onFilter && onFilter(checkedValues);
      }
    },
    [onFilter, loading],
  );

  const onResetCallback = useCallback(
    e => {
      setFilter([]);
      setShow(false);
      !!onReset && onReset(filter);
    },
    [onReset, filter],
  );

  const sortFilterGroup = useCallback(group => {
    if (Array.isArray(group) && group[0]?.key == "subject.name") {
      return group?.sort((a, b) =>
        a?.subject?.name.localeCompare(b?.subject?.name),
      );
    } else {
      return group;
    }
  }, []);

  const showOptions = arr => {
    if (Array.isArray(arr) && arr?.length > 0) {
      let groupAmount = 0;
      const hasOptionsGroup = arr?.some(el => {
        if (el?.group && Array.isArray(el?.group)) {
          groupAmount++;
          return el?.group;
        }
      });

      if (hasOptionsGroup && arr?.length == groupAmount) {
        let groupItemAmount = 0;
        const hasOptionsGroupItemEl = arr?.some(el => {
          if (Array.isArray(el?.group) && el?.group?.length > 1) {
            groupItemAmount++;
            return el?.group?.length > 1;
          }
        });
        return hasOptionsGroupItemEl;
      } else {
        return true;
      }
    }
  };

  const renderList = useCallback(
    arrayOptions => {
      const hasOptions = showOptions(arrayOptions);
      if (arrayOptions && Array.isArray(arrayOptions) && hasOptions) {
        return arrayOptions?.map((opt, inx, arr) => {
          if (opt?.group) {
            const hasGroupElem =
              Array.isArray(opt?.group) && opt?.group?.length > 1;
            const opened =
              filter &&
              filter.length > 0 &&
              filter.some(el => opt?.group.some(gr => gr.value == el));
            return (
              hasGroupElem && (
                <Collapse
                  key={`group-${opt.group[0]?.key}-${inx}`}
                  bordered={false}
                  defaultActiveKey={
                    opened ? `panel-${opt.group[0]?.key}-${inx}` : ""
                  }
                >
                  <Collapse.Panel
                    key={`panel-${opt.group[0]?.key}-${inx}`}
                    header={opt.group[0]?.label}
                  >
                    {opt?.group &&
                      opt?.group?.length > 0 &&
                      sortFilterGroup(opt?.group)?.map(
                        (elm, indx) =>
                          indx !== 0 &&
                          elm && (
                            <Checkbox
                              key={`${elm?.value}-option-${
                                opt.group[0]?.key
                              }-${indx}`}
                              value={elm?.value}
                            >
                              {elm?.label}
                            </Checkbox>
                          ),
                      )}
                  </Collapse.Panel>
                </Collapse>
              )
            );
          } else {
            return (
              opt?.label &&
              opt?.value && (
                <Checkbox
                  key={`${opt?.value}-option-${inx}`}
                  value={opt?.value}
                >
                  {opt?.label}
                </Checkbox>
              )
            );
          }
        });
      } else {
        return (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("errors.noData")}
          />
        );
      }
    },
    [filter, i18n.language],
  );

  const renderListAsTag = useCallback(
    arrayOptions => {
      const hasOptions = showOptions(arrayOptions);
      if (arrayOptions && Array.isArray(arrayOptions) && hasOptions) {
        return arrayOptions?.map((opt, inx, arr) => {
          if (opt?.group) {
            const hasGroupElem =
              Array.isArray(opt?.group) && opt?.group?.length > 1;
            const opened =
              filter &&
              filter.length > 0 &&
              filter.some(el => opt?.group.some(gr => gr.value == el));
            return (
              hasGroupElem && (
                <>
                  {opt?.group &&
                    opt?.group?.length > 0 &&
                    sortFilterGroup(opt?.group)?.map(
                      (elm, indx) =>
                        indx !== 0 &&
                        elm && (
                          <Tag.CheckableTag
                            key={`${elm?.value}-option-${
                              opt.group[0]?.key
                            }-${indx}`}
                            checked={filter.includes(elm?.value)}
                            onChange={() => {
                              const newFilter = [...filter];
                              const index = newFilter.indexOf(elm?.value);
                              if (index > -1) {
                                newFilter.splice(index, 1);
                              } else {
                                newFilter.push(elm?.value);
                              }
                              onFilterCallback(newFilter);
                            }}
                          >
                            {elm?.label}
                          </Tag.CheckableTag>
                        ),
                    )}
                </>
              )
            );
          } else {
            return (
              opt?.label &&
              opt?.value && (
                <Checkbox
                  key={`${opt?.value}-option-${inx}`}
                  value={opt?.value}
                >
                  {opt?.label}
                </Checkbox>
              )
            );
          }
        });
      } else {
        return (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("errors.noData")}
          />
        );
      }
    },
    [filter, i18n.language],
  );

  const iconRendering = useMemo(() => {
    return (
      <CustomIcons
        className={`filter-extra__icon ${
          show || filter?.length > 0 ? "show" : ""
        } ${loading ? "disabled" : ""}`}
        type="filter"
        onClick={toggleFilter}
      />
    );
  }, [toggleFilter, loading, show, filter]);

  const isTagsView = view == "tags";

  return (
    <div key={`filter-by-list-${uniqueKey}`} className={childClassNames}>
      {!!showIcon && iconRendering}
      <div
        className={`filter-extra__bar ${show ? "show" : ""} ${
          loading ? "disabled" : ""
        } ${!!isTagsView ? "tags-view" : ""}`}
      >
        {loading && (
          <div className="loading-overlay">
            <LoadingOutlined />
          </div>
        )}
        <Checkbox.Group
          name={`filter`}
          onChange={onFilterCallback}
          value={filter}
        >
          {!!!isTagsView ? renderList(options) : renderListAsTag(options)}
        </Checkbox.Group>
        {filter?.length > 0 && !loading && (
          <Button onClick={onResetCallback} className="reset-filter">
            <UndoOutlined /> {!!resetLabel && resetLabel || t("inputs.resetFilter")}
          </Button>
        )}
      </div>
    </div>
  );
}

FilterOptions.defaultProps = {
  uniqueKey: "simple",
  loading: false,
  view: "tags",
  showIcon: true,
};

export default FilterOptions;
